import * as React from "react";
import styled from "@emotion/styled";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import {
  Button,
  Container,
  FlexRow,
  H1,
  IconEditItem,
  IconNewImage,
  IconPalette,
  IconStar,
  IconUser,
  Link,
  LoadingSpinner,
  NoBreak,
  UnexpectedError,
} from "atoms";
import { AuthProps } from "features/auth";
import { canEditProfile } from "features/artistProfile";
import { useArtist } from "features/reviewable";
import { SiteFooter, SiteHeader } from "features/layouts";
import NotFound from "pages/NotFound";
import { ApiError } from "utils/api";
import { paths } from "utils/paths";
import { preventForwarding } from "styles/styleUtils";
import { useEditorState } from "../utils/useEditorContext";
import { GalleryBackButton } from "./GalleryBackButton";

type PortfolioPageTypeProps = {
  pathname: string;
  artistId: string;
  galleryId?: string;
  itemId?: string;
};
const portfolioPageType = ({
  pathname,
  artistId,
  galleryId,
  itemId,
}: PortfolioPageTypeProps) => {
  let editor = "";
  if (pathname === paths.artistFeaturedEdit(artistId)) {
    editor = "Featured Work";
  } else if (pathname === paths.artistPortfolioEdit(artistId)) {
    editor = "Portfolio";
  } else if (pathname === paths.artistGalleryEdit(artistId, galleryId ?? "")) {
    editor = "Gallery";
  } else if (pathname === paths.artistGalleryItemEdit(artistId, itemId ?? "")) {
    editor = "Item";
    // } else if (pathname === paths.artistAllGalleriesEdit(artistId)) {
    //   editor = "All Galleries";
    // } else if (pathname === paths.artistPortfolioAllItemsEdit(artistId)) {
    //   editor = "All Items";
  } else if (pathname === paths.artistProfileEditBeta(artistId)) {
    editor = "Profile Details";
  }
  return editor;
};

function LayoutPortfolioEditor({ user, roles }: AuthProps) {
  const location = useLocation();

  const {
    artistId = "",
    galleryId = "",
    itemId = "",
  } = useParams<{ artistId: string; galleryId: string; itemId: string }>();
  const [editorAction, setEditorAction, editorMeta] = useEditorState();
  const { data: artist, isError, error } = useArtist(artistId);

  const { pathname } = location;
  const editor = portfolioPageType({ pathname, artistId, galleryId, itemId });

  if (isError) {
    return (
      <Site>
        <SiteHeader isSticky={false} />
        <LayoutHeader editor={editor} setEditorAction={setEditorAction} />
        {error instanceof ApiError && error.status === 404 ? (
          <NotFound />
        ) : (
          <Main>
            <UnexpectedError error={error} />
          </Main>
        )}
        <SiteFooter />
      </Site>
    );
  }

  if (!artist) {
    return (
      <Site>
        <SiteHeader isSticky={false} />
        <LayoutHeader editor={editor} setEditorAction={setEditorAction} />
        <Main>
          <LoadingSpinner />
        </Main>
        <SiteFooter />
      </Site>
    );
  }

  const hasAccess = canEditProfile({ artist, user, roles });

  if (!hasAccess) {
    // TODO: show not authorized message?
    return <Navigate to={paths.artistProfile(artist.id)} replace />;
  }

  return (
    <Site>
      <SiteHeader isSticky={false} />
      <LayoutHeader editor={editor} setEditorAction={setEditorAction}>
        <ActionButtonRow>
          <ActionLink
            size="xxs"
            variant="secondary"
            to={paths.artistPortfolioEdit(artistId)}
            isActive={
              editor !== "Featured Work" && editor !== "Profile Details"
            }
          >
            <IconPalette />
            Portfolio
          </ActionLink>
          <ActionLink
            size="xxs"
            variant="secondary"
            to={paths.artistFeaturedEdit(artistId)}
            isActive={editor === "Featured Work"}
          >
            <IconStar />
            Featured
          </ActionLink>
          <ActionLink
            size="xxs"
            variant="secondary"
            to={paths.artistProfileEditBeta(artistId)}
            isActive={editor === "Profile Details"}
          >
            <IconUser />
            <NoBreak>Profile Details</NoBreak>
          </ActionLink>
        </ActionButtonRow>
      </LayoutHeader>
      <Main>
        <Container width="lg">
          <Outlet
            context={{ artist, editorAction, setEditorAction, ...editorMeta }}
          />
        </Container>
      </Main>
      <SiteFooter />
    </Site>
  );
}
const Site = styled.div({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const Main = styled.main(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  flex: "1 1 auto",
  paddingBlock: "2rem 3em",
}));

type LayoutHeaderProps = {
  children?: React.ReactNode;
  editor: string;
  setEditorAction: ReturnType<typeof useEditorState>[1];
  // add?: VoidFunction;
  // edit?: VoidFunction;
  // editItemOnItemPage?: VoidFunction;
};

function LayoutHeader({
  children,
  editor,
  setEditorAction,
}: LayoutHeaderProps) {
  const {
    artistId = "",
    galleryId = "",
    itemId = "",
  } = useParams<{ artistId: string; galleryId: string; itemId: string }>();

  return (
    <EditBar>
      <Header>
        <SContainer width="lg">
          {(editor === "Gallery" || editor === "Item") && (
            <GalleryBackButton
              itemId={itemId}
              artistId={artistId}
              galleryId={galleryId}
            />
          )}
          <Actions>{children}</Actions>
          <ProfileLinkRow gap="10px">
            <HelpLink
              size="xxxs"
              variant="secondary"
              to="https://www.canva.com/design/DAF-ICsUhAM/s6qIOVrfYwOZSIZilKrwfw/view"
              target="_blank"
              aria-label="Open Editor guide in new window"
            >
              ?
            </HelpLink>
            <ProfileLink
              size="xxxs"
              variant="secondary"
              to={paths.artistPortfolio(artistId)}
            >
              Back to Profile
            </ProfileLink>
          </ProfileLinkRow>
        </SContainer>
      </Header>
      <EditRow>
        <SContainer width="lg">
          {editor && <EditorTitle>{`Edit ${editor}`}</EditorTitle>}

          <FlexRow gap="10px" justifyContent="center">
            {editor === "Gallery" && (
              <>
                <ModalButton
                  size="xxs"
                  variant="secondary"
                  onClick={() => setEditorAction("add")}
                >
                  Add Items
                  <IconNewImage />
                </ModalButton>
                <ModalButton
                  size="xxs"
                  variant="secondary"
                  onClick={() => setEditorAction("edit")}
                >
                  Edit Details
                  <IconEditItem />
                </ModalButton>
              </>
            )}
            {editor === "Item" && (
              <ModalButton
                size="xxs"
                variant="secondary"
                onClick={() =>
                  setEditorAction("edit", { itemId: "", itemType: "item" })
                }
              >
                Edit Item
                <IconEditItem />
              </ModalButton>
            )}
          </FlexRow>
        </SContainer>
      </EditRow>
    </EditBar>
  );
}

const EditBar = styled.div(({ theme }) => ({
  position: "sticky",
  top: 0,
  flex: "0 0 auto",
  width: "100%",
  zIndex: 400,
}));
const Header = styled.header(({ theme }) => ({
  background: theme.colors.accent3,
  color: theme.colors.fg,
  paddingTop: "env(safe-area-inset-top)",

  [theme.breakpoints.desktop]: {
    padding: 0,
  },
}));

const SContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.maxDesktop]: {
    paddingInline: "1.5rem",
  },
  alignItems: "center",
  position: "relative",
  zIndex: 3,
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "5px 1rem",
  [theme.breakpoints.desktop]: {
    justifyContent: "flex-start",
  },
}));

const EditRow = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: theme.colors.bg,
  boxShadow: theme.boxShadow.light,
  paddingBlock: "15px 14px",
}));
const EditorTitle = styled(H1)(({ theme }) => ({
  color: theme.colors.fg,
  fontSize: theme.fontSizes.md,
  margin: 0,
  marginRight: "auto",
}));

const ProfileLinkRow = styled(FlexRow)(({ theme }) => ({
  order: 1,
  marginLeft: "auto",
  [theme.breakpoints.desktop]: {
    order: 3,
    marginLeft: 0,
  },
}));
const ProfileLink = styled(Link)(({ theme }) => ({
  backgroundColor: theme.colors.transparent,
  padding: "4px 10px 3px",
  marginBlock: "1em",
  lineHeight: 1,
}));
const HelpLink = styled(ProfileLink)(({ theme }) => ({
  // aspectRatio: "1",
  fontSize: 14,
  padding: "3px 6px 2px",
}));

const Actions = styled(FlexRow)(({ theme }) => ({
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.colors.accent3,
  gridArea: "actions",
  justifyContent: "center",
  paddingBottom: 0,
  flex: "0 0 100%",
  order: 3,
  [theme.breakpoints.desktop]: {
    backgroundColor: theme.colors.transparent,
    position: "relative",
    flex: "0 0 auto",
    order: 2,
    marginRight: "auto",
  },
}));

const ActionButtonRow = styled(FlexRow)(({ theme }) => ({
  flexWrap: "nowrap",
  justifyContent: "center",
  gap: 0,
  width: "100%",
  [theme.breakpoints.desktop]: {
    flex: "auto",
    gap: 2,
    order: 2,
  },
})).withComponent("nav");

const ActionLink = styled(Link, {
  shouldForwardProp: preventForwarding("isActive"),
})<{ isActive: boolean }>(({ isActive, theme }) => ({
  ...theme.fonts.body,
  background: theme.colors.transparent,
  border: 0,
  borderRadius: 0,
  flexDirection: "column",
  fontSize: 14,
  fontWeight: "normal",
  height: 70,
  minWidth: "max-content",
  paddingInline: "0",
  paddingBlock: "1em",
  textTransform: "none",
  "&&": { flex: "1 1 calc(100% / 3)" },
  svg: {
    display: "block",
    width: 18,
    maxWidth: 18,
    maxHeight: 18,
  },
  "&:hover, &:focus-visible": {
    background: `${theme.colors.bg}55`,
    color: theme.colors.fg,
  },
  ...(isActive && {
    "&,&:hover": {
      backgroundColor: `${theme.colors.bg}55`,
    },
  }),
  // [theme.breakpoints.tablet]: {},
  [theme.breakpoints.desktop]: {
    ...theme.fonts.display,
    flexDirection: "row",
    flex: "auto",
    fontSize: 16,
    height: "auto",
    marginBottom: -2,
    textTransform: "uppercase",
    borderTopLeftRadius: theme.borderRadius.sm,
    borderTopRightRadius: theme.borderRadius.sm,
    paddingBlock: ".9em 1.1em",
    paddingInline: "1.25em",
    marginTop: 6,
    "&&": { flex: "1" },
    ...(isActive && {
      "&,&:hover": {
        backgroundColor: theme.colors.bg,
      },
    }),
    svg: { order: 99 },
  },
}));

const ModalButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.maxXs]: {
    paddingInline: ".75em",
  },
}));

export { LayoutPortfolioEditor };
